
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import vPreLoader from "@/components/vPreLoader.vue";
import vModal from "@/components/vModal.vue";
import AllergenMLRepository from "@/utils/API/AllergenMLRepository";
import AllergenRepository from "@/utils/API/AllergenRepository";
import { MlAnalysisResponse } from "@/models/AllergenML/AllergenML";
import { NutrientSource } from "@/models/NutrientSource/NutrientSource";
import { formatCountrySpecificAllergenName } from "@/utils/AllergenDisplayNames";
import {
  buildMatrixRows,
  MatrixColumn,
  matrixColumns as defaultMatrixColumns,
  matrixRowDedupeKey,
  MatrixRow,
  resolvePresentationAllergenKey,
} from "@/components/allergens/allergenOverrideMatrixLogic";

@Component({
  components: {
    vPreLoader,
    vModal,
  },
})
export default class AllergenOverrideMatrix extends Vue {
  @Prop({ default: null }) analysisResult!: MlAnalysisResponse | null;
  @Prop({ required: true }) targetType!: "ingredient" | "recipe" | "combined-recipe";
  @Prop({ required: true }) targetId!: number;
  @Prop({ required: true }) restaurantId!: number;
  @Prop({ default: NutrientSource.US }) country!: NutrientSource;
  @Prop({ default: "" }) targetName!: string;
  @Prop({ default: true }) showHelpButtons!: boolean;
  @Prop({ default: false }) showCloseButton!: boolean;

  matrixColumns = defaultMatrixColumns;
  loadingDefinitions = false;
  errorMessage = "";
  savingOverrideKey = "";
  matrixRowOrderByDedupeKey: Record<string, number> = {};
  nextMatrixRowOrderIndex = 0;
  countryAllergenDefinitions: Array<any> = [];
  visibleAllergenDefinitions: Array<any> = [];
  showOverrideScrollLeft = false;
  showOverrideScrollRight = false;
  showOverrideScrollUp = false;
  showOverrideScrollDown = false;
  optionalBadgeRefreshKey = 0;
  handleWindowResize = () => {
    this.updateOverrideGridScrollCues();
    this.optionalBadgeRefreshKey += 1;
  };
  overrideFloatingTooltip = {
    visible: false,
    content: "",
    placement: "top" as "top" | "bottom",
    left: 0,
    top: 0,
    width: 720,
    minWidth: 320,
  };
  overrideConfirmation: {
    isOpen: boolean;
    row: MatrixRow | null;
    column: MatrixColumn | null;
    isAllergen: boolean;
    allergenCode: string;
    ingredientName: string;
    allergenLabel: string;
    wasAiDetected: boolean;
  } = {
    isOpen: false,
    row: null,
    column: null,
    isAllergen: false,
    allergenCode: "",
    ingredientName: "",
    allergenLabel: "",
    wasAiDetected: false,
  };

  get translations() {
    return (this.$store.state as any)?.LocalizationModule?.translations;
  }

  t(key: string, fallback: string, ...args: Array<string | number>) {
    const translation = this.translations?.get?.(key, ...args);
    return translation && translation !== "---" ? translation : fallback;
  }

  get isGeneralIngredientScope() {
    return this.targetType === "ingredient";
  }

  get selectedCountryLabel() {
    if (this.isGeneralIngredientScope) {
      return this.t("allergens_ai_scope_all_allergens", "All Allergens");
    }

    switch (this.country) {
    case NutrientSource.Canada:
      return "Canada";
    case NutrientSource.UK:
      return "UK";
    case NutrientSource.US:
    default:
      return "USA";
    }
  }

  get overrideConfirmationModalTitle() {
    return this.overrideConfirmation.wasAiDetected
      ? this.t("allergens_ai_override_modal_review", "Review Allergen Override")
      : this.t("allergens_ai_override_modal_confirm", "Confirm Allergen Override");
  }

  get overrideConfirmationIntro() {
    return this.overrideConfirmation.wasAiDetected
      ? this.t("allergens_ai_override_intro_review", "You are about to override an AI-detected allergen for ")
      : this.t("allergens_ai_override_intro_confirm", "Please confirm you want to override the AI detection for ");
  }

  get overrideConfirmationQuestion() {
    return this.overrideConfirmation.wasAiDetected
      ? this.t("allergens_ai_override_question_review", "This manual decision will replace the AI result and will be used for allergen declarations.")
      : this.t("allergens_ai_override_question_confirm", "This manual decision will be used for allergen declarations.");
  }

  get overviewUsageTooltip() {
    return this.t("allergens_ai_overview_tooltip", "Hover over an allergen name to review common reaction symptoms.");
  }

  get overviewLegendTooltipHtml() {
    return [
      "<div class='allergens-popover-legend'>",
      `<div class='allergens-popover-legend-row'><span class='allergens-popover-badge ai'><i class='fa fa-check'></i></span><span>${this.t("allergens_ai_legend_ai", "AI detected this allergen.")}</span></div>`,
      `<div class='allergens-popover-legend-row'><span class='allergens-popover-badge removed'><i class='fa fa-times'></i></span><span>${this.t("allergens_ai_legend_removed", "Manually marked as not an allergen.")}</span></div>`,
      `<div class='allergens-popover-legend-row'><span class='allergens-popover-badge user'><i class='fa fa-check'></i><span class='allergens-popover-badge-accent'><i class='fa fa-question'></i></span></span><span>${this.t("allergens_ai_legend_user_added", "Manually added as an allergen.")}</span></div>`,
      "</div>",
    ].join("");
  }

  get overrideFloatingTooltipStyle() {
    return {
      left: `${this.overrideFloatingTooltip.left}px`,
      top: `${this.overrideFloatingTooltip.top}px`,
      width: `${this.overrideFloatingTooltip.width}px`,
      minWidth: `${this.overrideFloatingTooltip.minWidth}px`,
      transform: this.overrideFloatingTooltip.placement === "bottom"
        ? "translate(-50%, 0)"
        : "translate(-50%, -100%)",
    };
  }

  mounted() {
    this.captureMatrixRowOrder(this.analysisResult);
    void this.fetchAllergenCatalog();
    window.addEventListener("resize", this.handleWindowResize);
  }

  beforeDestroy() {
    window.removeEventListener("resize", this.handleWindowResize);
  }

  @Watch("country")
  async onCountryChanged() {
    await this.fetchAllergenCatalog();
  }

  @Watch("analysisResult")
  onAnalysisChanged() {
    this.captureMatrixRowOrder(this.analysisResult);
    this.$nextTick(() => this.updateOverrideGridScrollCues());
  }

  get matrixRows(): MatrixRow[] {
    return buildMatrixRows(
      this.analysisResult,
      this.country,
      this.t.bind(this),
      this.normalizeCountryPresentationAllergenKey.bind(this),
      this.matrixRowOrder.bind(this),
    );
  }

  get countryReportableAllergenKeys() {
    if (this.isGeneralIngredientScope) {
      return new Set<string>();
    }

    return new Set<string>(
      this.countryAllergenDefinitions
        .map((allergen) => this.normalizeCountryPresentationAllergenKey(resolvePresentationAllergenKey(allergen.code || "", allergen.name || allergen.displayName || "")))
        .filter((key) => key !== "")
    );
  }

  get visibleAllergenKeys() {
    return new Set<string>(
      this.visibleAllergenDefinitions
        .map((allergen) => this.normalizeCountryPresentationAllergenKey(resolvePresentationAllergenKey(allergen.code || "", allergen.name || allergen.displayName || "")))
        .filter((key) => key !== "")
    );
  }

  get countryOptionalOverrideKeys() {
    if (this.isGeneralIngredientScope) {
      return new Set<string>();
    }

    if (this.country === NutrientSource.US) {
      return new Set<string>(["molluscs", "gluten", "mustard", "sulphites", "celery", "lupin"]);
    }

    if (this.country === NutrientSource.Canada) {
      return new Set<string>(["gluten", "celery", "lupin"]);
    }

    return new Set<string>();
  }

  get overrideMatrixColumns(): MatrixColumn[] {
    return this.matrixColumns
      .filter((column) => this.isReportableOverrideColumn(column) || this.isOptionalVisibleAllergenKey(column.key))
      .map((column) => this.resolveCountryMatrixColumn(column))
      .sort((left, right) => this.sortOptionalColumns(left, right))
      .map((column, index, columns) => ({
        ...column,
        isFirstOptionalAllergen: this.isFirstOptionalGroupColumn(column, index, columns)
      }));
  }

  get hasOverrideOptionalAllergens() {
    return this.overrideMatrixColumns.some((column) => column.isOptionalAllergen);
  }

  getElementRef(refName: string): HTMLElement | null {
    const ref = this.$refs[refName];
    const element = Array.isArray(ref) ? ref[0] : ref;
    return element instanceof HTMLElement ? element : null;
  }

  getOverrideGridScroller() {
    return this.getElementRef("overrideGridScroller");
  }

  resolveCountryMatrixColumn(column: MatrixColumn): MatrixColumn {
    if (this.isGeneralIngredientScope) {
      return {
        ...column,
        label: formatCountrySpecificAllergenName(column.label),
        displayLabel: column.displayLabel
          ? formatCountrySpecificAllergenName(column.displayLabel)
          : column.displayLabel,
        isOptionalAllergen: false,
        optionalGroupKey: this.getOptionalGroupKey(column.key),
      };
    }

    if (this.country === NutrientSource.UK && column.key === "gluten") {
      return {
        ...column,
        label: "Cereals containing Gluten",
        displayLabel: "Cereals containing Gluten",
        isOptionalAllergen: this.isOptionalVisibleAllergenKey(column.key),
        optionalGroupKey: this.getOptionalGroupKey(column.key),
      };
    }

    return {
      ...column,
      label: formatCountrySpecificAllergenName(column.label, this.country),
      displayLabel: column.displayLabel
        ? formatCountrySpecificAllergenName(column.displayLabel, this.country)
        : column.displayLabel,
      isOptionalAllergen: this.isOptionalVisibleAllergenKey(column.key),
      optionalGroupKey: this.getOptionalGroupKey(column.key),
    };
  }

  getOptionalGroupKey(allergenKey: string) {
    if (this.isGeneralIngredientScope) {
      return `optional-${allergenKey}`;
    }

    if (this.country === NutrientSource.US) {
      if (allergenKey === "molluscs") {
        return "optional-us-mollusks";
      }

      if (["gluten", "mustard", "sulphites"].includes(allergenKey)) {
        return "optional-us-gluten-mustard-sulfites";
      }

      if (["celery", "lupin"].includes(allergenKey)) {
        return "optional-us-celery-lupin";
      }
    }

    if (this.country === NutrientSource.Canada) {
      if (allergenKey === "gluten") {
        return "optional-ca-gluten";
      }

      if (["celery", "lupin"].includes(allergenKey)) {
        return "optional-ca-celery-lupin";
      }
    }

    return `optional-${allergenKey}`;
  }

  getOptionalGroupOrder(column: MatrixColumn) {
    if (!column.isOptionalAllergen) {
      return -1;
    }

    if (this.isGeneralIngredientScope) {
      return 99;
    }

    const optionalGroupKey = column.optionalGroupKey || this.getOptionalGroupKey(column.key);

    if (this.country === NutrientSource.US) {
      if (optionalGroupKey === "optional-us-mollusks") {
        return 0;
      }

      if (optionalGroupKey === "optional-us-gluten-mustard-sulfites") {
        return 1;
      }

      if (optionalGroupKey === "optional-us-celery-lupin") {
        return 2;
      }
    }

    if (this.country === NutrientSource.Canada) {
      if (optionalGroupKey === "optional-ca-gluten") {
        return 0;
      }

      if (optionalGroupKey === "optional-ca-celery-lupin") {
        return 1;
      }
    }

    return 99;
  }

  sortOptionalColumns(left: MatrixColumn, right: MatrixColumn) {
    if (left.isOptionalAllergen !== right.isOptionalAllergen) {
      return left.isOptionalAllergen ? 1 : -1;
    }

    if (!left.isOptionalAllergen && !right.isOptionalAllergen) {
      return 0;
    }

    const optionalGroupOrderDifference = this.getOptionalGroupOrder(left) - this.getOptionalGroupOrder(right);

    if (optionalGroupOrderDifference !== 0) {
      return optionalGroupOrderDifference;
    }

    return 0;
  }

  isFirstOptionalGroupColumn(column: MatrixColumn, index: number, columns: MatrixColumn[]) {
    if (!column.isOptionalAllergen) {
      return false;
    }

    const previousColumn = columns[index - 1];

    if (!previousColumn?.isOptionalAllergen) {
      return true;
    }

    return previousColumn.optionalGroupKey !== column.optionalGroupKey;
  }

  normalizeCountryPresentationAllergenKey(allergenKey: string) {
    if (this.isGeneralIngredientScope) {
      return allergenKey;
    }

    if (this.country === NutrientSource.UK && allergenKey === "wheat") {
      return "gluten";
    }

    return allergenKey;
  }

  isReportableAllergenKey(allergenKey: string) {
    const reportableKeys = this.countryReportableAllergenKeys;
    const optionalOverrideKeys = this.countryOptionalOverrideKeys;

    if (optionalOverrideKeys.has(allergenKey)) {
      return false;
    }

    return reportableKeys.size === 0 || reportableKeys.has(allergenKey);
  }

  isOptionalVisibleAllergenKey(allergenKey: string) {
    const reportableKeys = this.countryReportableAllergenKeys;
    const optionalOverrideKeys = this.countryOptionalOverrideKeys;

    if (optionalOverrideKeys.has(allergenKey)) {
      return this.visibleAllergenKeys.has(allergenKey);
    }

    return reportableKeys.size > 0
      && this.visibleAllergenKeys.has(allergenKey)
      && !reportableKeys.has(allergenKey);
  }

  isReportableOverrideColumn(column: MatrixColumn) {
    return this.isReportableAllergenKey(column.key);
  }

  countOptionalColumns(columns: MatrixColumn[]) {
    return columns.filter((column) => column.isOptionalAllergen).length;
  }

  countCountryRuleColumns(columns: MatrixColumn[]) {
    return columns.filter((column) => !column.isOptionalAllergen).length;
  }

  getMeasuredOptionalFloatingBadgeStyle() {
    const shell = this.getElementRef("overrideMatrixShell");
    const scroller = this.getOverrideGridScroller();

    if (!shell || !scroller) {
      return null;
    }

    const optionalHeadings = Array.from(scroller.querySelectorAll<HTMLElement>(".allergens-column-heading-optional"));

    if (optionalHeadings.length === 0) {
      return null;
    }

    const shellRect = shell.getBoundingClientRect();
    const firstOptionalRect = optionalHeadings[0].getBoundingClientRect();
    const lastOptionalRect = optionalHeadings[optionalHeadings.length - 1].getBoundingClientRect();
    const optionalCenter = ((firstOptionalRect.left + lastOptionalRect.right) / 2) - shellRect.left;

    return {
      left: `${optionalCenter}px`,
    };
  }

  getOptionalFloatingBadgeStyle(columns: MatrixColumn[]) {
    void this.optionalBadgeRefreshKey;

    const measuredStyle = this.getMeasuredOptionalFloatingBadgeStyle();
    if (measuredStyle) {
      return measuredStyle;
    }

    const ingredientColumnWidth = 280;
    const allergenColumnWidth = 92;
    const optionalColumnCount = this.countOptionalColumns(columns);
    const countryColumnCount = this.countCountryRuleColumns(columns);

    return {
      left: `${ingredientColumnWidth + (countryColumnCount * allergenColumnWidth) + ((optionalColumnCount * allergenColumnWidth) / 2)}px`,
    };
  }

  updateOverrideGridScrollCues() {
    const scroller = this.getOverrideGridScroller();

    if (!scroller) {
      this.showOverrideScrollLeft = false;
      this.showOverrideScrollRight = false;
      this.showOverrideScrollUp = false;
      this.showOverrideScrollDown = false;
      return;
    }

    const maxLeft = Math.max(scroller.scrollWidth - scroller.clientWidth, 0);
    const maxTop = Math.max(scroller.scrollHeight - scroller.clientHeight, 0);
    this.showOverrideScrollLeft = scroller.scrollLeft > 8;
    this.showOverrideScrollRight = scroller.scrollLeft < maxLeft - 8;
    this.showOverrideScrollUp = scroller.scrollTop > 8;
    this.showOverrideScrollDown = scroller.scrollTop < maxTop - 8;
  }

  handleOverrideGridScroll() {
    this.updateOverrideGridScrollCues();
    this.optionalBadgeRefreshKey += 1;
  }

  scrollOverrideGrid(direction: "left" | "right" | "up" | "down") {
    const scroller = this.getOverrideGridScroller();

    if (!scroller) {
      return;
    }

    const horizontalOffset = Math.max(scroller.clientWidth * 0.55, 260);
    const verticalOffset = Math.max(scroller.clientHeight * 0.55, 180);

    scroller.scrollBy({
      left: direction === "right" ? horizontalOffset : direction === "left" ? -horizontalOffset : 0,
      top: direction === "down" ? verticalOffset : direction === "up" ? -verticalOffset : 0,
      behavior: "smooth",
    });

    window.setTimeout(() => this.updateOverrideGridScrollCues(), 240);
  }

  async fetchAllergenCatalog() {
    this.loadingDefinitions = true;
    this.errorMessage = "";

    try {
      const visibleResponse = await AllergenRepository.listAllergens();

      if (this.isGeneralIngredientScope) {
        this.countryAllergenDefinitions = [];
        this.visibleAllergenDefinitions = visibleResponse.data || [];
        this.$nextTick(() => this.updateOverrideGridScrollCues());
        return;
      }

      const countryResponse = await AllergenRepository.listAllergensByCountry(Number(this.country));

      this.countryAllergenDefinitions = countryResponse.data || [];
      this.visibleAllergenDefinitions = visibleResponse.data || [];
      this.$nextTick(() => this.updateOverrideGridScrollCues());
    }
    catch (error: any) {
      this.errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.errorMessages?.[0]?.userVisibleMessage ||
        error?.message ||
        this.t("allergens_ai_override_save_failed", "Unable to load allergen override options.");
    }
    finally {
      this.loadingDefinitions = false;
    }
  }

  showOverrideFloatingTooltip(
    event: FocusEvent | MouseEvent,
    content: string,
    _isHtml = false,
    placement: "top" | "bottom" = "top",
    width = 720,
    minWidth = 320
  ) {
    void _isHtml;

    const target = event.currentTarget as HTMLElement | null;
    if (!target || !content) {
      return;
    }

    const rect = target.getBoundingClientRect();
    const maxWidth = width;
    const viewportPadding = 16;
    const rawLeft = rect.left + (rect.width / 2);
    const left = Math.min(
      Math.max(rawLeft, (maxWidth / 2) + viewportPadding),
      window.innerWidth - (maxWidth / 2) - viewportPadding
    );
    const top = placement === "bottom"
      ? rect.bottom + 14
      : rect.top - 14;

    this.overrideFloatingTooltip = {
      visible: true,
      content,
      placement,
      left,
      top,
      width,
      minWidth,
    };
  }

  hideOverrideFloatingTooltip() {
    this.overrideFloatingTooltip.visible = false;
  }

  captureMatrixRowOrder(analysisResult: MlAnalysisResponse | null) {
    (analysisResult?.ingredients || []).forEach((ingredient) => {
      const dedupeKey = ingredient.ingredientId
        ? `ingredient-${ingredient.ingredientId}`
        : `ingredient-name-${(ingredient.ingredientName || "").trim().toLowerCase()}`;

      if (this.matrixRowOrderByDedupeKey[dedupeKey] !== undefined) {
        return;
      }

      this.matrixRowOrderByDedupeKey[dedupeKey] = this.nextMatrixRowOrderIndex;
      this.nextMatrixRowOrderIndex += 1;
    });
  }

  matrixRowOrder(row: MatrixRow) {
    const dedupeKey = matrixRowDedupeKey(row);
    const existingOrder = this.matrixRowOrderByDedupeKey[dedupeKey];

    if (existingOrder !== undefined) {
      return existingOrder;
    }

    this.matrixRowOrderByDedupeKey[dedupeKey] = this.nextMatrixRowOrderIndex;
    this.nextMatrixRowOrderIndex += 1;
    return this.matrixRowOrderByDedupeKey[dedupeKey];
  }

  getAllergenColumnTooltip(column: MatrixColumn) {
    const scope = column.isOptionalAllergen
      ? this.t("allergens_ai_tooltip_optional_scope", "Optional allergen. This is visible in the allergen catalog but is not part of the selected country rule.")
      : this.t("allergens_ai_tooltip_country_scope", "Country-rule allergen.");

    return this.t(
      "allergens_ai_column_tooltip",
      `${column.label}. ${scope} Common reaction notes: ${this.t("allergens_ai_tooltip_fallback_symptoms", "Review ingredient evidence and product specifications for symptoms and allergen risk.")}`,
      column.label,
      scope,
      this.t("allergens_ai_tooltip_fallback_symptoms", "Review ingredient evidence and product specifications for symptoms and allergen risk.")
    );
  }

  getOverrideIconClass(row: MatrixRow, column: MatrixColumn) {
    const state = row.cellStates[column.key];

    if (state?.status === "user-removed") {
      return "fa fa-times";
    }

    return row.flagged.indexOf(column.key) !== -1 ? "fa fa-check" : "fa fa-plus";
  }

  getOverrideBadgeClass(row: MatrixRow, column: MatrixColumn) {
    const state = row.cellStates[column.key];

    if (state?.status === "user-removed") {
      return "allergen-badge-removed";
    }

    if (state?.status === "user-added") {
      return "allergen-badge-user";
    }

    return row.flagged.indexOf(column.key) !== -1 ? "allergen-badge-ai" : column.colorClass;
  }

  getOverrideCellTooltip(row: MatrixRow, column: MatrixColumn) {
    const state = row.cellStates[column.key];

    if (state) {
      return state.tooltip;
    }

    if (row.flagged.indexOf(column.key) !== -1) {
      return this.t("allergens_ai_override_tooltip_disable", `Disable ${column.label}`, column.label);
    }

    return this.t("allergens_ai_override_tooltip_mark_as", `Mark as ${column.label}`, column.label);
  }

  resolveOverrideAllergenCode(row: MatrixRow, column: MatrixColumn, isAllergen: boolean) {
    if (isAllergen) {
      return column.allergenCode;
    }

    return row.flaggedAllergenCodes[column.key]?.[0] || column.allergenCode;
  }

  overrideKey(row: MatrixRow, column: MatrixColumn) {
    return `${row.key}-${column.key}`;
  }

  isOverrideSaving(row: MatrixRow, column: MatrixColumn) {
    return this.savingOverrideKey === this.overrideKey(row, column);
  }

  openAllergenOverrideConfirmation(row: MatrixRow, column: MatrixColumn) {
    if (!this.analysisResult || !row.ingredientId || this.savingOverrideKey) {
      return;
    }

    if (!this.isReportableOverrideColumn(column) && !column.isOptionalAllergen) {
      this.$snotify.error(
        this.t(
          "allergens_ai_not_reportable_for_country",
          `${column.label} is not reportable for ${this.selectedCountryLabel}.`,
          column.label,
          this.selectedCountryLabel
        )
      );
      return;
    }

    const wasFlagged = row.flagged.indexOf(column.key) !== -1;
    const isAllergen = !wasFlagged;
    const allergenCode = this.resolveOverrideAllergenCode(row, column, isAllergen);
    const currentState = row.cellStates[column.key];
    const wasAiDetected = currentState?.status === "ai";

    this.overrideConfirmation = {
      isOpen: true,
      row,
      column,
      isAllergen,
      allergenCode,
      ingredientName: row.ingredient,
      allergenLabel: column.label,
      wasAiDetected,
    };
  }

  closeAllergenOverrideConfirmation() {
    if (this.savingOverrideKey) {
      return;
    }

    this.resetAllergenOverrideConfirmation();
  }

  resetAllergenOverrideConfirmation() {
    this.overrideConfirmation = {
      isOpen: false,
      row: null,
      column: null,
      isAllergen: false,
      allergenCode: "",
      ingredientName: "",
      allergenLabel: "",
      wasAiDetected: false,
    };
  }

  async confirmAllergenOverride() {
    const { row, column, isAllergen, allergenCode } = this.overrideConfirmation;

    if (!row || !column) {
      this.closeAllergenOverrideConfirmation();
      return;
    }

    if (!this.isReportableOverrideColumn(column) && !column.isOptionalAllergen) {
      this.$snotify.error(
        this.t(
          "allergens_ai_not_reportable_for_country",
          `${column.label} is not reportable for ${this.selectedCountryLabel}.`,
          column.label,
          this.selectedCountryLabel
        )
      );
      this.resetAllergenOverrideConfirmation();
      return;
    }

    await this.toggleAllergenOverride(row, column, isAllergen, allergenCode);
  }

  async toggleAllergenOverride(row: MatrixRow, column: MatrixColumn, isAllergen: boolean, allergenCode: string) {
    if (!this.analysisResult || !row.ingredientId || this.savingOverrideKey) {
      return;
    }

    const overrideKey = this.overrideKey(row, column);
    this.savingOverrideKey = overrideKey;

    try {
      const response = this.targetType === "ingredient"
        ? await AllergenMLRepository.overrideIngredientAllergen({
          restaurantId: this.restaurantId,
          ingredientId: row.ingredientId,
          countryId: this.isGeneralIngredientScope ? undefined : this.country,
          nutrientSource: this.isGeneralIngredientScope ? undefined : this.country,
          allergenCode,
          isAllergen,
          reason: isAllergen
            ? this.t(
              "allergens_ai_override_reason_add_column",
              `Marked ${column.label} as an allergen from AI Allergen Detector.`,
              column.label
            )
            : this.t(
              "allergens_ai_override_reason_remove_column",
              `Disabled ${column.label} as an allergen from AI Allergen Detector.`,
              column.label
            ),
        })
        : await AllergenMLRepository.overrideRecipeIngredientAllergen({
          restaurantId: this.restaurantId,
          recipeId: this.targetId,
          ingredientId: row.ingredientId,
          countryId: this.country,
          nutrientSource: this.country,
          allergenCode,
          isAllergen,
          reason: isAllergen
            ? this.t(
              "allergens_ai_override_reason_add_ingredient",
              `Marked ${row.ingredient} as ${column.label} from AI Allergen Detector.`,
              row.ingredient,
              column.label
            )
            : this.t(
              "allergens_ai_override_reason_remove_ingredient",
              `Disabled ${column.label} for ${row.ingredient} from AI Allergen Detector.`,
              column.label,
              row.ingredient
            ),
        });

      this.$emit("update:analysisResult", response.data);
      this.$snotify.success(
        isAllergen
          ? this.t("allergens_ai_override_applied", "Allergen override applied.")
          : this.t("allergens_ai_override_disabled", "Allergen disabled for this item.")
      );
      this.resetAllergenOverrideConfirmation();
      this.$nextTick(() => this.updateOverrideGridScrollCues());
    }
    catch (error: any) {
      const errorMessage = error?.response?.data?.message
        || error?.response?.data?.errorMessages?.[0]?.userVisibleMessage
        || error?.message
        || this.t("allergens_ai_override_save_failed", "The allergen override could not be saved.");

      this.$snotify.error(errorMessage);
    }
    finally {
      if (this.savingOverrideKey === overrideKey) {
        this.savingOverrideKey = "";
      }
    }
  }
}
